/**
 * Per-sentence citation marker (CT6, Citation Traceability pack).
 *
 * Rendered AFTER every claim sentence the LLM emitted in
 * `report.claims[]`. The marker is a small superscript `[¹]` / `[²]`
 * styled like a footnote reference. Click → popover showing the
 * resolved sources for that claim (lineage refs from CT2's hook).
 *
 * Composes with the existing inline `<RefLink>` (square `[Ref N]` pill)
 * and `<ReliabilityBadge>` (coloured glyph) — the three render side by
 * side without overlapping concerns:
 *
 *   RefLink         — answer "what's at appendix slot N?"
 *   ReliabilityBadge — answer "verified / inferred / unverified?"
 *   ClaimMarker      — answer "WHAT EVIDENCE backed this sentence?"
 *                     (provides the structured lineage view)
 *
 * Back-compat: legacy reports without `claims[]` simply don't get
 * these markers — the existing prose + RefLink rendering is unchanged.
 *
 * The popover is intentionally lightweight (native `title` + click-to-
 * expand). Stage d (CT10) will replace it with the universal
 * EvidenceLineageInspector modal. Until then this is the simplest
 * surface that delivers per-sentence click-to-reveal.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { parseClaimSource } from '../../../types/citation';
import type { DataSource } from '../../../types/siteRag';
import {
  isRetiredSimulationDataSource,
  LEGACY_SIMULATION_REFERENCE_LABEL,
  LEGACY_SIMULATION_REGEN_GUIDANCE,
} from '../../../lib/reportGeneration/legacySimulationRetirement';

/** Render ordinary ASCII digits inside the superscripted citation button so
 *  embedded report fonts never emit .notdef/NUL glyphs for Unicode ⁰ or ⁴–⁹. */
export function formatSuperscriptIndex(n: number): string {
  if (!Number.isFinite(n) || n < 1) return '';
  return String(Math.floor(n));
}

export interface ClaimMarkerProps {
  /** 1-based index in the report's claims[] array — what the reader sees. */
  index: number;
  /** Raw lineage source refs (`'ref:3'`, `'doc:abc'`, …). */
  sources: string[];
  /** The report's references list — used to resolve `ref:N` sources to
   *  their human-readable source/url for the popover. */
  references?: DataSource[];
  /** Optional: the claim sentence text. Surfaced in the popover header
   *  so the reader sees which sentence the marker pertains to. */
  text?: string;
}

/**
 * Render a `[¹]` superscript marker after a sentence. Click toggles a
 * tiny inline popover listing the resolved sources.
 */
export function ClaimMarker({ index, sources, references, text }: ClaimMarkerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click — keeps the popover lightweight without a
  // global focus trap.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const onClick = useCallback((): void => setOpen(v => !v), []);

  const resolved = (sources ?? []).map(raw => resolveClaimSource(raw, references));
  // Build a compact `title` tooltip — the hover affordance for
  // readers who don't click. Truncated for long source lists.
  const titleText = `[${index}] ${
    resolved.length === 0
      ? '(no sources)'
      : resolved.slice(0, 3).map(r => r.label).join(' · ') + (resolved.length > 3 ? ` · +${resolved.length - 3} more` : '')
  }`;

  if (resolved.length > 0 && resolved.every((source) => source.retired === true)) {
    return (
      <span
        className="inline-flex align-super whitespace-nowrap text-[0.7em] text-muted-foreground ml-0.5"
        title={LEGACY_SIMULATION_REGEN_GUIDANCE}
        data-claim-marker
        data-claim-index={index}
        data-retired-simulation-reference="true"
      >
        {LEGACY_SIMULATION_REFERENCE_LABEL}
      </span>
    );
  }

  return (
    <span
      className="inline-block align-baseline relative"
      data-claim-marker
      data-claim-index={index}
    >
      <button
        type="button"
        onClick={onClick}
        title={titleText}
        aria-label={`Show sources for claim ${index}`}
        // House-style 2026-06-29 — citation markers render in the Hanken APPARATUS
        // face + teal accent (the academic-editorial `sup.cite`). The vars resolve
        // on the report root (`--report-apparatus-font` / `--report-accent`); outside
        // a report they fall back to inherit / currentColor so the marker stays legible.
        style={{
          fontFamily: "var(--report-apparatus-font, 'Hanken Grotesk', Inter, sans-serif)",
          color: 'var(--report-accent, currentColor)',
          fontWeight: 600,
          lineHeight: 1,
        }}
        className="inline-block text-[0.72em] hover:underline cursor-pointer px-0.5 align-super select-none whitespace-nowrap"
      >
        [{formatSuperscriptIndex(index)}]
      </button>
      {open ? (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={`Sources for claim ${index}`}
          className="absolute z-50 mt-1 left-0 min-w-[min(280px,calc(100vw-2rem))] max-w-[min(420px,calc(100vw-2rem))] rounded-md border border-border bg-popover text-popover-foreground p-3 shadow-md text-xs"
        >
          {text ? (
            <div className="font-medium text-foreground/90 mb-2 line-clamp-3 leading-snug">
              <span className="text-muted-foreground mr-1">[{index}]</span>
              {text}
            </div>
          ) : null}
          {resolved.length === 0 ? (
            <div className="text-muted-foreground italic">No sources attached.</div>
          ) : (
            <ul className="space-y-1.5">
              {resolved.map((r, i) => (
                <li key={i} className="leading-snug">
                  <span className="inline-block uppercase tracking-wide text-[0.65rem] text-muted-foreground mr-1.5">
                    {r.kindLabel}
                  </span>
                  <span className="text-foreground">{r.label}</span>
                  {r.detail ? (
                    <span className="block text-muted-foreground mt-0.5">{r.detail}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </span>
  );
}

/* ── Resolver ─────────────────────────────────────────────────────────── */

interface ResolvedSource {
  kindLabel: string;
  label: string;
  detail?: string;
  retired?: boolean;
}

/**
 * Map a `ReportClaim.sources` ref string to a human-readable label.
 * Best-effort: `ref:N` resolves through `references[n-1]`; other
 * prefixes show the raw payload (Stage 3 widens this via the CT2
 * lineage hook).
 */
function resolveClaimSource(raw: string, references?: DataSource[]): ResolvedSource {
  const parsed = parseClaimSource(raw);
  if (!parsed) {
    return { kindLabel: 'unknown', label: raw };
  }
  switch (parsed.prefix) {
    case 'ref:': {
      const n = Number(parsed.payload);
      const ref = Number.isFinite(n) && n >= 1 && references ? references[n - 1] : undefined;
      if (isRetiredSimulationDataSource(ref)) {
        return {
          kindLabel: 'retired evidence',
          label: LEGACY_SIMULATION_REFERENCE_LABEL,
          detail: LEGACY_SIMULATION_REGEN_GUIDANCE,
          retired: true,
        };
      }
      return ref
        ? { kindLabel: 'ref', label: `[Ref ${n}] ${ref.source}`, detail: ref.url ?? undefined }
        : { kindLabel: 'ref', label: `[Ref ${parsed.payload}]`, detail: 'Not found in appendix' };
    }
    case 'doc:':
      return { kindLabel: 'document', label: parsed.payload };
    case 'sed:':
      return { kindLabel: 'sed variable', label: parsed.payload };
    case 'sed-section:':
      return { kindLabel: 'sed section', label: parsed.payload };
    case 'provider:':
      return { kindLabel: 'provider', label: parsed.payload };
    case 'site-context:':
      return { kindLabel: 'site context', label: parsed.payload };
    case 'baseline:':
      return { kindLabel: 'baseline', label: parsed.payload };
    case 'bopSim:':
    case 'bopAnnual:':
    case 'bopLcoe:':
      return {
        kindLabel: 'retired evidence',
        label: LEGACY_SIMULATION_REFERENCE_LABEL,
        detail: LEGACY_SIMULATION_REGEN_GUIDANCE,
        retired: true,
      };
    case 'annualYield:':
      return { kindLabel: 'annual yield', label: parsed.payload };
    case 'financeLcoe:':
      return { kindLabel: 'finance lcoe', label: parsed.payload };
    // CSE (Claims Structured Emission) — three new lineage-target
    // primitives. We render a best-effort label here; Stage 3 widens
    // these to first-class resolution via the CT2 lineage hook.
    case 'kmz:':
      return { kindLabel: 'kmz layer', label: parsed.payload };
    case 'report:':
      return { kindLabel: 'prior report', label: parsed.payload };
    case 'dam:':
      return { kindLabel: 'dam entry', label: parsed.payload };
    default:
      return { kindLabel: parsed.prefix.replace(/:$/, ''), label: parsed.payload };
  }
}
